import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {makeAnalyticsEventId} from 'analytics/Analytics';
import {RESULTS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {RIGHT_ARROW} from 'lookup-config/LookupConfigModalConstants';
import {AssayRunSchema} from 'results/assay-schemas/AssaySchemaConstants';
import Tooltip from 'ui-library/bs/Tooltip';
import * as Colors from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import runModal from 'ui-library/runModal';
import {capitalize, constantCaseToWords} from 'util/NameUtil';

import {automationFileConfigStyles, SectionNumber} from './AutomationFileConfigUtils';
import EditableConfigOptionDisplay from './EditableConfigOptionDisplay';
import JsonMapperConfigModal from './JsonMapperConfigModal';
import {
  AutomationInputFileConfigSpec,
  ClientAutomationOutputFileConfigSpec,
  LabAutomationTransform,
} from './RunSchemaSpecConstants';
import LabAutomationTransformsModal from './transforms-modal/LabAutomationTransformsModal';

export interface AutomationFileConfigEditorTransformsSectionProps<
  T extends ClientAutomationOutputFileConfigSpec | AutomationInputFileConfigSpec,
> {
  allowPreview?: boolean;
  automationFileConfig: T;
  baseSchema: AssayRunSchema;
  configFileType: string;
  description: string;
  onUpdateConfig: (newConfig: T) => void;
  sectionNumber: number;
  title: string;
  children?: React.ReactNode;
}

export default function AutomationFileConfigEditorTransformsSection<
  T extends ClientAutomationOutputFileConfigSpec | AutomationInputFileConfigSpec,
>(props: AutomationFileConfigEditorTransformsSectionProps<T>): JSX.Element {
  const transforms = props.automationFileConfig.transforms;
  const transformDataType = (props.automationFileConfig as ClientAutomationOutputFileConfigSpec)
    ?.transformDataType;
  const [configJson, setConfigJson] = useState(
    (props.automationFileConfig as ClientAutomationOutputFileConfigSpec)?.jsonMapperConfigJson
  );

  function openModal(): void {
    const analyticsEventGroupId = makeAnalyticsEventId();
    const analyticsMetadata = {
      event_group_id: analyticsEventGroupId,
      schema_type: 'run_schema',
      config_file_type: props.configFileType,
      schema_id: props.baseSchema.id,
    };
    analytics.trackEvent(RESULTS_ANALYTICS_ACTIONS.STARTED_TRANSFORM_CONFIGURATION, analyticsMetadata);
    runModal(LabAutomationTransformsModal, {
      allowPreview: props.allowPreview,
      baseSchema: props.baseSchema,
      initialTransforms: transforms,
      automationFileConfig: props.automationFileConfig,
      analyticsMetadata,
      onRequestSubmit: (transforms: Array<LabAutomationTransform>) => {
        props.onUpdateConfig({
          ...props.automationFileConfig,
          transforms,
        });
      },
    });
  }
  function openJsonMapperModal(): void {
    runModal(JsonMapperConfigModal, {
      initialConfigJson: (props.automationFileConfig as ClientAutomationOutputFileConfigSpec)
        ?.jsonMapperConfigJson,
      onRequestSubmit: (configJson: string) => {
        setConfigJson(configJson);
        (props.automationFileConfig as ClientAutomationOutputFileConfigSpec).jsonMapperConfigJson =
          configJson;
      },
    });
  }

  return (
    <div className={css(automationFileConfigStyles.subsection, automationFileConfigStyles.subsectionBorder)}>
      <SectionNumber sectionNumber={props.sectionNumber} />
      <div className={css(automationFileConfigStyles.innerTitle)}>{props.title}</div>
      {transformDataType?.toLowerCase() === 'custom' && (
        <div>
          <div
            className={css(
              automationFileConfigStyles.description,
              automationFileConfigStyles.sectionDescription
            )}
          >
            Tell Benchling how to transform the instrument ASM JSON to CSV.
          </div>
          <a onClick={openJsonMapperModal}>Configure JSON mapping</a>
          {['', null, undefined].includes(configJson) ? (
            <Tooltip title="Must configure a JSON mapping when instrument data type is set to Custom">
              <BenchlingIcon icon="exclamation" className={css(styles.jsonConfigNotSet)} />
            </Tooltip>
          ) : (
            <BenchlingIcon icon="status-success" className={css(styles.jsonConfigSet)} />
          )}
          <br />
          <br />
        </div>
      )}
      <div
        className={css(automationFileConfigStyles.description, automationFileConfigStyles.sectionDescription)}
      >
        {props.description}
      </div>
      {transforms ? (
        <div className={css(styles.existingTransformsSection)}>
          <div className={css(styles.subsection)}>
            <FieldLabel label="Transforms" labelStyle={LabelStyle.LABEL} />
            <br />
            <EditableConfigOptionDisplay
              onEdit={openModal}
              onRemove={() => {
                const newConfig = {...props.automationFileConfig};
                delete newConfig.transforms;
                props.onUpdateConfig(newConfig);
              }}
              editButtonLabel="Edit transforms"
              removeButtonLabel="Remove transforms"
            >
              {transforms
                .map((transform) => capitalize(constantCaseToWords(transform.transformType!)))
                .join(` ${RIGHT_ARROW} `)}
            </EditableConfigOptionDisplay>
          </div>
          <div className={css(styles.subsection)}>{props.children}</div>
        </div>
      ) : (
        <a onClick={openModal}>Set transforms</a>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  existingTransformsSection: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  subsection: {
    flex: 0.33,
    marginRight: '32px',
  },
  jsonConfigSet: {
    marginLeft: 5,
    color: Colors.GREEN,
  },
  jsonConfigNotSet: {
    marginLeft: 5,
    color: Colors.RED,
  },
});
